import {
  ABORT_EVENT,
  SSE_CONTENT_TYPE,
  SSE_DATA_LINE_DELIMITER,
  SSE_DATA_PREFIX,
  SSE_ENCODING,
  SSE_EVENT_DELIMITER,
  SSE_EVENT_PREFIX,
  SSE_ID_PREFIX,
} from "./constants";
import { SSEController, SSEMessage, StartSSEProps } from "./types";

/**
 * Starts an SSE (Server-Sent Events) connection and streams events to the provided callback.
 */
export const startSSE = ({
  url,
  method = "GET",
  headers = {},
  body = null,
  signal,
  onEvent,
}: StartSSEProps): SSEController => {
  const controller = new AbortController();
  const linked = linkAbortSignals(signal, controller.signal);

  void (async () => {
    try {
      const res = await fetch(url, {
        method,
        headers: {
          Accept: SSE_CONTENT_TYPE,
          ...headers,
        },
        body,
        signal: linked,
      });

      if (!res.ok || !res.body) {
        onEvent({
          type: "error",
          error: new Error(`Bad response: ${res.status}`),
        });

        return;
      }

      onEvent({ type: "open" });

      const reader = res.body.getReader();
      const decoder = new TextDecoder(SSE_ENCODING);
      let buffer = "";
      let bytes = 0;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        if (!value) continue;

        bytes += value.byteLength;
        buffer += decoder.decode(value, { stream: true });

        let delimiterIndex = buffer.indexOf(SSE_EVENT_DELIMITER);
        while (delimiterIndex !== -1) {
          const rawEvent = buffer.slice(0, delimiterIndex);
          buffer = buffer.slice(delimiterIndex + SSE_EVENT_DELIMITER.length);
          const evt = parseSSE(rawEvent);
          if (evt) onEvent({ type: "message", message: evt, bytes });

          delimiterIndex = buffer.indexOf(SSE_EVENT_DELIMITER);
        }
      }

      if (buffer.trim().length > 0) {
        const evt = parseSSE(buffer);
        if (evt) onEvent({ type: "message", message: evt, bytes });
      }

      onEvent({ type: "end" });
    } catch (error) {
      if ((error as any)?.name === "AbortError") {
        onEvent({ type: "end" });
      } else {
        onEvent({ type: "error", error });
      }
    }
  })();

  return {
    abort: () => controller.abort(),
  };
};

/**
 * Links multiple abort signals so that aborting any one of them aborts the returned signal.
 */
const linkAbortSignals = (
  a?: AbortSignal,
  b?: AbortSignal
): AbortSignal | undefined => {
  if (!a && !b) return undefined;

  const controller = new AbortController();
  const onAbort = () => controller.abort();
  a?.addEventListener(ABORT_EVENT, onAbort);
  b?.addEventListener(ABORT_EVENT, onAbort);
  if (a?.aborted || b?.aborted) controller.abort();

  return controller.signal;
};

/**
 * Parses a raw SSE event string into an SSEMessage object with data, id, and event fields.
 */
const parseSSE = (raw: string): SSEMessage | null => {
  const lines = raw.split(/\r?\n/);
  let dataLines: string[] = [];
  let id: string | undefined;
  let event: string | undefined;

  for (const line of lines) {
    if (line.startsWith(SSE_DATA_PREFIX)) {
      dataLines.push(line.slice(SSE_DATA_PREFIX.length).trimStart());
    } else if (line.startsWith(SSE_ID_PREFIX)) {
      id = line.slice(SSE_ID_PREFIX.length).trimStart();
    } else if (line.startsWith(SSE_EVENT_PREFIX)) {
      event = line.slice(SSE_EVENT_PREFIX.length).trimStart();
    }
  }

  const data = dataLines.join(SSE_DATA_LINE_DELIMITER);
  if (!data) return null;

  return { data, id, event };
};
